<template>
  <div class="ticket-detail">
    <!-- Header -->
    <div class="mb-6">
      <button
        @click="$emit('back')"
        class="btn btn-ghost btn-sm mb-4"
        style="color: var(--text-secondary);">
        <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Tickets
      </button>

      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap mb-2">
            <span class="text-caption font-mono" style="color: var(--text-tertiary);">{{ ticket?.ticket_number }}</span>
            <span :class="['badge', statusBadgeClass(ticket?.status)]">
              {{ statusLabel(ticket?.status) }}
            </span>
            <span class="badge badge-default">
              {{ categoryLabel(ticket?.category) }}
            </span>
          </div>
          <h1 class="heading-4" style="color: var(--text-primary);">
            {{ ticket?.subject }}
          </h1>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="card">
      <div class="p-6 flex items-center justify-center">
        <div class="spinner" />
        <span class="ml-3 text-body-sm" style="color: var(--text-secondary);">Loading conversation...</span>
      </div>
    </div>

    <template v-else>
      <!-- Conversation Card -->
      <div class="card mb-6">
        <div class="card-header">
          <h3 class="card-title">
            Conversation
          </h3>
          <span class="badge badge-default">{{ messages.length }} messages</span>
        </div>

        <!-- Messages -->
        <div ref="messagesContainer" class="divide-y max-h-[500px] overflow-y-auto" style="border-color: var(--border-default);">
          <div
            v-for="message in messages"
            :key="message.id"
            class="p-6"
            :style="message.message_type === 'admin_reply' ? 'background-color: var(--bg-surface-sunken);' : ''">
            <div class="flex items-start gap-4">
              <!-- Avatar -->
              <div class="flex-shrink-0">
                <div
                  class="avatar"
                  :class="message.message_type === 'admin_reply' ? 'avatar-primary' : ''">
                  {{ message.author_name?.substring(0, 2).toUpperCase() }}
                </div>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between gap-4 mb-2">
                  <div class="flex items-center gap-2">
                    <span class="text-body-sm font-medium" style="color: var(--text-primary);">
                      {{ message.author_name }}
                    </span>
                    <span v-if="message.message_type === 'admin_reply'" class="badge badge-primary">
                      Support
                    </span>
                  </div>
                  <span class="text-caption flex-shrink-0" style="color: var(--text-tertiary);">
                    {{ formatDate(message.created_at) }}
                  </span>
                </div>

                <div class="text-body" style="color: var(--text-secondary); white-space: pre-wrap;">
                  {{ message.body }}
                </div>

                <!-- Attachments -->
                <div v-if="message.attachments?.length > 0" class="mt-4">
                  <!-- Image Attachments -->
                  <div v-if="getImageAttachments(message.attachments).length > 0" class="flex flex-wrap gap-3 mb-3">
                    <a
                      v-for="attachment in getImageAttachments(message.attachments)"
                      :key="attachment.id"
                      :href="attachment.url"
                      target="_blank"
                      class="block rounded-lg overflow-hidden border transition-shadow hover:shadow-md"
                      style="border-color: var(--border-default);">
                      <img
                        :src="attachment.url"
                        :alt="attachment.filename"
                        class="max-w-[200px] max-h-[150px] object-cover"
                        loading="lazy">
                    </a>
                  </div>
                  <!-- Non-Image Attachments -->
                  <div v-if="getNonImageAttachments(message.attachments).length > 0" class="flex flex-wrap gap-2">
                    <a
                      v-for="attachment in getNonImageAttachments(message.attachments)"
                      :key="attachment.id"
                      :href="attachment.url"
                      target="_blank"
                      class="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-caption transition-colors"
                      style="background-color: var(--bg-muted); color: var(--text-secondary);">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <span>{{ attachment.filename }}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Resolved Notice -->
        <div v-if="ticket?.status === 'resolved' || ticket?.status === 'closed'" class="p-6 text-center" style="background-color: var(--bg-surface-sunken);">
          <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full text-body-sm font-medium" style="background-color: var(--color-success-100, #dcfce7); color: var(--color-success-700, #15803d);">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            This ticket has been {{ ticket?.status === 'resolved' ? 'resolved' : 'closed' }}
          </div>
        </div>
      </div>

      <!-- Reply Form -->
      <div v-if="canReply" class="card">
        <div class="card-header">
          <h3 class="card-title">
            Reply
          </h3>
        </div>
        <div class="card-body">
          <div class="form-group">
            <textarea
              ref="replyInput"
              v-model="replyText"
              rows="4"
              class="form-textarea"
              placeholder="Type your reply..."
              @keydown.meta.enter="sendReply"
              @keydown.ctrl.enter="sendReply" />
          </div>

          <!-- File Upload -->
          <div class="form-group">
            <label class="form-label">Attachments (optional)</label>
            <div class="flex items-center gap-4">
              <label class="btn btn-secondary btn-sm cursor-pointer">
                <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                Add Files
                <input
                  type="file"
                  multiple
                  class="hidden"
                  @change="onFileSelect">
              </label>
              <span v-if="attachments.length > 0" class="text-body-sm" style="color: var(--text-secondary);">
                {{ attachments.length }} file(s) selected
              </span>
            </div>
          </div>

          <!-- Attached Files Preview -->
          <div v-if="attachments.length > 0" class="mb-4">
            <!-- Image Previews -->
            <div v-if="pendingImageAttachments.length > 0" class="flex flex-wrap gap-3 mb-3">
              <div
                v-for="(file, index) in pendingImageAttachments"
                :key="'img-' + index"
                class="relative group">
                <img
                  :src="getFilePreviewUrl(file)"
                  :alt="file.name"
                  class="w-16 h-16 object-cover rounded-lg border"
                  style="border-color: var(--border-default);">
                <button
                  @click="removeAttachment(attachments.indexOf(file))"
                  class="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style="background-color: var(--color-error, #ef4444); color: white;">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <!-- Non-Image Files -->
            <div v-if="pendingNonImageAttachments.length > 0" class="flex flex-wrap gap-2">
              <div
                v-for="(file, index) in pendingNonImageAttachments"
                :key="'file-' + index"
                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-caption"
                style="background-color: var(--bg-muted); color: var(--text-secondary);">
                <span class="truncate max-w-[150px]">{{ file.name }}</span>
                <button @click="removeAttachment(attachments.indexOf(file))" class="hover:opacity-70" style="color: var(--text-tertiary);">
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="alert alert-error mb-4">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{{ error }}</span>
          </div>
        </div>

        <div class="card-footer flex items-center justify-between">
          <span class="text-caption" style="color: var(--text-tertiary);">
            Press Cmd+Enter to send
          </span>
          <button
            @click="sendReply"
            :disabled="!canSend || sending"
            class="btn btn-primary">
            <svg v-if="sending" class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            {{ sending ? 'Sending...' : 'Send Reply' }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { getSupportTicket, addTicketReply } from '~/javascript/api/support_tickets.js'
import consumer from '~/javascript/channels/consumer'

// Props
const props = defineProps({
  ticketId: {
    type: String,
    required: true
  }
})

// Emits
defineEmits(['back'])

// State
const ticket = ref(null)
const messages = ref([])
const loading = ref(true)
const replyText = ref('')
const attachments = ref([])
const sending = ref(false)
const error = ref(null)
const messagesContainer = ref(null)
const subscription = ref(null)

// Constants
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']

// Computed
const canReply = computed(() => ticket.value && !['closed'].includes(ticket.value.status))

const canSend = computed(() => replyText.value.trim().length > 0 || attachments.value.length > 0)

const pendingImageAttachments = computed(() => attachments.value.filter((f) => f.type.startsWith('image/')))

const pendingNonImageAttachments = computed(() => attachments.value.filter((f) => !f.type.startsWith('image/')))

// Methods
const fetchTicket = async() => {
  try {
    loading.value = true
    const response = await getSupportTicket(props.ticketId)
    ticket.value = response.data.ticket
    messages.value = response.data.messages
    await nextTick()
    scrollToBottom()
  } catch (error) {
    console.error('Error fetching ticket:', error) // eslint-disable-line no-console
  } finally {
    loading.value = false
  }
}

const sendReply = async() => {
  if (!canSend.value || sending.value) return

  try {
    sending.value = true
    const response = await addTicketReply(props.ticketId, {
      body: replyText.value,
      attachments: attachments.value
    })

    if (response.data.success) {
      // Only add if not already present (might have arrived via ActionCable first)
      const newMessage = response.data.message
      if (!messages.value.find((m) => m.id === newMessage.id)) {
        messages.value.push(newMessage)
      }
      ticket.value = response.data.ticket
      replyText.value = ''
      attachments.value = []
      await nextTick()
      scrollToBottom()
    }
  } catch (error) {
    console.error('Error sending reply:', error) // eslint-disable-line no-console
  } finally {
    sending.value = false
  }
}

const onFileSelect = (event) => {
  const files = Array.from(event.target.files)
  validateAndAddFiles(files)
  event.target.value = ''
}

const validateAndAddFiles = (files) => {
  error.value = null
  const validFiles = files.filter((file) => {
    if (file.size > MAX_FILE_SIZE) {
      error.value = `File "${file.name}" exceeds 10MB limit`
      return false
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      error.value = `File "${file.name}" is not an allowed type (images and PDFs only)`
      return false
    }
    return true
  })
  attachments.value = [...attachments.value, ...validFiles]
}

const removeAttachment = (index) => {
  attachments.value.splice(index, 1)
}

const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

const formatDate = (date) => {
  if (!date) return 'Unknown'
  const d = new Date(date)
  const now = new Date()
  const diffInMinutes = Math.floor((now - d) / 1000 / 60)

  if (diffInMinutes < 1) return 'Just now'
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60)
    return `${hours}h ago`
  }

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

const statusLabel = (status) => {
  const labels = {
    open: 'Open',
    in_progress: 'In Progress',
    waiting_on_customer: 'Awaiting Reply',
    resolved: 'Resolved',
    closed: 'Closed'
  }
  return labels[status] || status
}

const statusBadgeClass = (status) => {
  const classes = {
    open: 'badge-primary',
    in_progress: 'badge-warning',
    waiting_on_customer: 'badge-default',
    resolved: 'badge-success',
    closed: 'badge-dark'
  }
  return classes[status] || 'badge-default'
}

const categoryLabel = (category) => {
  const labels = {
    content_change: 'Content Change',
    bug_report: 'Bug Report',
    question: 'Question',
    feature_request: 'Feature Request'
  }
  return labels[category] || category
}

const isImageAttachment = (attachment) => {
  const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  return imageTypes.includes(attachment.content_type)
}

const getImageAttachments = (attachments) => attachments.filter(isImageAttachment)

const getNonImageAttachments = (attachments) => attachments.filter((a) => !isImageAttachment(a))

const getFilePreviewUrl = (file) => URL.createObjectURL(file)

const subscribeToChannel = () => {
  subscription.value = consumer.subscriptions.create(
    { channel: 'SupportTicketChannel', ticket_id: props.ticketId },
    {
      received(data) {
        switch (data.event) {
          case 'message_added':
          // Only add if not already in list (prevent duplicates from own messages)
            if (!messages.value.find((m) => m.id === data.message.id)) {
              messages.value.push(data.message)
              nextTick(() => scrollToBottom())
            }
            break
          case 'status_changed':
          // Backend sends ticket object and to_status
            if (data.ticket) {
              ticket.value = data.ticket
            } else if (data.to_status) {
              ticket.value = { ...ticket.value, status: data.to_status }
            }
            break
        }
      }
    }
  )
}

const unsubscribeFromChannel = () => {
  if (subscription.value) {
    subscription.value.unsubscribe()
    subscription.value = null
  }
}

// Watchers
watch(() => props.ticketId, () => {
  unsubscribeFromChannel()
  fetchTicket()
  subscribeToChannel()
})

// Lifecycle
onMounted(() => {
  fetchTicket()
  subscribeToChannel()
})

onUnmounted(() => {
  unsubscribeFromChannel()
})
</script>
